/*
 * SL — Modal "Producto añadido" (Fase 5).
 *
 * Arquitectura: se engancha al evento nativo PUB_SUB_EVENTS.cartUpdate que
 * ya publica assets/product-form.js tras cada add-to-cart real (source:
 * 'product-form'), con el payload que Shopify ya devuelve en /cart/add.js.
 * No se crea ninguna llamada nueva a /cart/add.js ni un segundo listener de
 * submit: PDP, sticky desktop/mobile y recomendaciones ya pasan todos por el
 * mismo <product-form>, así que un único punto de escucha basta y evita
 * cualquier riesgo de doble alta.
 *
 * El drawer nativo (assets/cart-drawer.js) sigue actualizando su contenido
 * con normalidad en cada cartUpdate (fallback técnico intacto); lo único que
 * se suprime es su apertura visual automática, envolviendo
 * CartDrawer.prototype.open desde fuera, sin tocar el archivo nativo.
 */
(function () {
  'use strict';

  var THRESHOLD_CENTS = 6000;

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || window.SLShopMoneyFormat || '{{amount_with_comma_separator}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = typeof precision === 'undefined' ? 2 : precision;
      thousands = typeof thousands === 'undefined' ? ',' : thousands;
      decimal = typeof decimal === 'undefined' ? '.' : decimal;

      if (isNaN(number) || number == null) return 0;

      number = (number / 100.0).toFixed(precision);

      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';

      return dollars + centsPart;
    }

    var match = formatString.match(placeholderRegex);
    var key = match ? match[1] : 'amount';

    switch (key) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      default:
        value = formatWithDelimiters(cents, 2, '.', ',');
    }

    return match ? formatString.replace(placeholderRegex, value) : value;
  }

  // Formato español fijo para el importe de la barra de envío gratis
  // ("51,01 €", nunca "€51,01" ni "51,01 EUR"), independiente de
  // window.SLShopMoneyFormat. Solo se usa aquí — formatMoney() de arriba
  // sigue igual para el resto del modal (precio del producto añadido, etc.).
  function formatFreeShippingAmount(cents) {
    var amount = (Math.max(cents, 0) / 100).toFixed(2);
    var parts = amount.split('.');
    var integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return integerPart + ',' + parts[1] + ' €';
  }

  function updateFreeShippingBars(totalCents) {
    var remaining = Math.max(THRESHOLD_CENTS - totalCents, 0);
    var percent = Math.min(Math.max((totalCents / THRESHOLD_CENTS) * 100, 0), 100);
    var complete = remaining <= 0;

    document.querySelectorAll('[data-sl-free-shipping-bar]').forEach(function (bar) {
      var text = bar.querySelector('[data-sl-free-shipping-text]');
      var fill = bar.querySelector('[data-sl-free-shipping-fill]');
      var marker = bar.querySelector('[data-sl-free-shipping-marker]');
      var track = bar.querySelector('.sl-free-shipping-bar__track');

      bar.classList.toggle('sl-free-shipping-bar--complete', complete);

      if (text) {
        text.innerHTML = complete
          ? '<strong>¡Ya tienes envío gratis!</strong>'
          : 'Te faltan <strong>' + formatFreeShippingAmount(remaining) + '</strong> para conseguir envío gratis';
      }
      if (fill) fill.style.width = percent + '%';
      if (marker) marker.style.left = percent + '%';
      if (track) track.setAttribute('aria-valuenow', Math.round(percent));
    });
  }

  function fetchCartTotal() {
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    return fetch(cartUrl + '.js')
      .then(function (response) {
        return response.json();
      })
      .catch(function () {
        return null;
      });
  }

  // Suprime solo la apertura visual automática del drawer nativo cuando este
  // modal toma el relevo; su contenido se sigue actualizando siempre porque
  // no tocamos renderContents(), solo open(). No modifica cart-drawer.js.
  var suppressNextDrawerOpen = false;

  if (window.customElements) {
    customElements.whenDefined('cart-drawer').then(function () {
      var CartDrawer = customElements.get('cart-drawer');
      var originalOpen = CartDrawer.prototype.open;
      CartDrawer.prototype.open = function (triggeredBy) {
        if (suppressNextDrawerOpen) {
          suppressNextDrawerOpen = false;
          return;
        }
        return originalOpen.call(this, triggeredBy);
      };
    });
  }

  document.addEventListener('DOMContentLoaded', init);
  if (document.readyState !== 'loading') init();

  function init() {
    var modal = document.querySelector('[data-sl-cart-modal]');
    if (!modal) return;
    if (modal.dataset.slInitialized) return;
    modal.dataset.slInitialized = 'true';

    var panel = modal.querySelector('.sl-cart-modal__panel');
    var openedBy = null;
    var lastAddedCrossSellItem = null;
    var closeTimer = null;

    function isOpen() {
      return !modal.hidden;
    }

    function openModal() {
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
      openedBy = document.activeElement;
      modal.hidden = false;
      document.body.classList.add('overflow-hidden');
      window.requestAnimationFrame(function () {
        modal.classList.add('sl-cart-modal--visible');
      });
      document.addEventListener('keydown', onKeydown);
      window.setTimeout(function () {
        trapFocus(panel, modal.querySelector('.sl-cart-modal__close'));
      }, 50);
    }

    function closeModal() {
      if (!isOpen()) return;
      modal.classList.remove('sl-cart-modal--visible');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', onKeydown);
      removeTrapFocus(openedBy);
      closeTimer = window.setTimeout(function () {
        modal.hidden = true;
      }, 220);
    }

    function onKeydown(event) {
      if (event.code && event.code.toUpperCase() === 'ESCAPE') {
        closeModal();
      }
    }

    modal.querySelectorAll('[data-sl-cart-modal-close]').forEach(function (el) {
      el.addEventListener('click', closeModal);
    });

    function populateProduct(item) {
      var img = modal.querySelector('[data-sl-cart-modal-image]');
      var name = modal.querySelector('[data-sl-cart-modal-name]');
      var variant = modal.querySelector('[data-sl-cart-modal-variant]');
      var qty = modal.querySelector('[data-sl-cart-modal-qty]');
      var price = modal.querySelector('[data-sl-cart-modal-price]');

      if (img) {
        var imageUrl = item.image || (item.featured_image && item.featured_image.url) || '';
        if (imageUrl) imageUrl = imageUrl.replace(/(\.[a-zA-Z0-9]+)(\?.*)?$/, '_200x$1$2');
        img.src = imageUrl;
        img.alt = item.product_title || item.title || '';
      }
      if (name) name.textContent = item.product_title || item.title || '';
      if (variant) {
        var variantTitle = item.variant_title;
        if (variantTitle && variantTitle !== 'Default Title') {
          variant.textContent = variantTitle;
          variant.hidden = false;
        } else {
          variant.hidden = true;
          variant.textContent = '';
        }
      }
      if (qty) qty.textContent = 'Cantidad: ' + item.quantity;
      if (price) {
        var unit = formatMoney(item.final_price);
        price.textContent = item.quantity > 1 ? unit + ' × ' + item.quantity : unit;
      }
    }

    function loadCrossSell(productId) {
      var container = modal.querySelector('[data-sl-cart-modal-cross-sell]');
      var list = modal.querySelector('[data-sl-cart-modal-cross-sell-list]');
      var baseUrl = modal.dataset.recommendationsUrl;
      if (!container || !list || !baseUrl || !productId) return;

      fetch(baseUrl + '?section_id=cart-drawer&product_id=' + productId + '&limit=3&intent=complementary')
        .then(function (response) {
          return response.text();
        })
        .then(function (text) {
          var html = new DOMParser().parseFromString(text, 'text/html');
          var sourceList = html.querySelector('.complementary-products-list');
          if (!sourceList || !sourceList.querySelector('.grid__item')) {
            container.hidden = true;
            list.innerHTML = '';
            return;
          }
          list.innerHTML = sourceList.innerHTML;
          container.hidden = false;
        })
        .catch(function () {
          container.hidden = true;
        });
    }

    modal.addEventListener('click', function (event) {
      var crossSellList = modal.querySelector('[data-sl-cart-modal-cross-sell-list]');
      if (!crossSellList || !crossSellList.contains(event.target)) return;
      var submitButton = event.target.closest('button[type="submit"]');
      if (!submitButton) return;
      lastAddedCrossSellItem = submitButton.closest('.grid__item');
    });

    function markCrossSellAdded() {
      if (!lastAddedCrossSellItem) return;
      var el = lastAddedCrossSellItem;
      el.classList.add('sl-cart-modal__cross-sell-item--added');
      window.setTimeout(function () {
        el.classList.remove('sl-cart-modal__cross-sell-item--added');
      }, 1800);
      lastAddedCrossSellItem = null;
    }

    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event.source !== 'product-form') return;

      suppressNextDrawerOpen = true;

      var item = event.cartData;
      var alreadyOpen = isOpen();

      if (item && item.product_id) {
        populateProduct(item);
        loadCrossSell(item.product_id);
      }

      fetchCartTotal().then(function (cart) {
        if (cart) updateFreeShippingBars(cart.total_price);
      });

      if (alreadyOpen) {
        markCrossSellAdded();
      } else {
        openModal();
      }
    });
  }
})();
